import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useLanguage } from '../context/LanguageContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../components/ui/select';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import {
  Dumbbell, ArrowLeft, Users, Plus, Check, X, Square,
  ChevronRight, AlertTriangle, Loader2, Save, TrendingUp,
} from 'lucide-react';

const STATUS_COLORS = {
  ACTIVE: 'bg-emerald-500/20 text-emerald-400 border-emerald-500/30',
  STOPPED: 'bg-amber-500/20 text-amber-400 border-amber-500/30',
  COMPLETED: 'bg-sky-500/20 text-sky-400 border-sky-500/30',
  EXCLUDED: 'bg-zinc-600/20 text-zinc-500 border-zinc-600/30',
};

export default function GroupVBTTestPage() {
  const { api } = useAuth();
  const { language } = useLanguage();
  const navigate = useNavigate();
  const t = (en, it) => language === 'IT' ? it : en;

  const [step, setStep] = useState('setup'); // setup | testing | results
  const [teams, setTeams] = useState([]);
  const [athletes, setAthletes] = useState([]);
  const [exercises, setExercises] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  // Setup form
  const [setup, setSetup] = useState({ team_id: '', date: new Date().toISOString().slice(0, 10), exercise_id: '', custom_lift_name: '', notes: '' });
  const [selectedAthletes, setSelectedAthletes] = useState({});

  // Testing state
  const [session, setSession] = useState(null);
  const [currentLoad, setCurrentLoad] = useState('');
  const [athleteInputs, setAthleteInputs] = useState({});
  const [results, setResults] = useState(null);
  const [comparisons, setComparisons] = useState({});

  useEffect(() => {
    api.get('/teams').then(r => setTeams(r.data)).catch(() => {});
    api.get('/prescriptions/library?category=STRENGTH').then(r => setExercises(r.data)).catch(() => {});
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const loadAthletes = useCallback(async (teamId) => {
    if (!teamId) return;
    try {
      const r = await api.get(`/athletes?team_id=${teamId}`);
      setAthletes(r.data);
      const sel = {};
      r.data.forEach(a => { sel[a.id] = true; });
      setSelectedAthletes(sel);
    } catch { /* ignore */ }
  }, [api]);

  useEffect(() => { if (setup.team_id) loadAthletes(setup.team_id); }, [setup.team_id, loadAthletes]);

  const handleStartSession = async () => {
    if (!setup.team_id || !setup.exercise_id) return;
    const activeIds = Object.entries(selectedAthletes).filter(([, v]) => v).map(([k]) => k);
    if (activeIds.length < 1) return;
    setLoading(true);
    try {
      const r = await api.post('/vbt/group-sessions', { ...setup, athlete_ids: activeIds });
      setSession(r.data);
      setStep('testing');
    } catch (e) { alert(e.response?.data?.detail || e.message); }
    setLoading(false);
  };

  const addLoadStep = async () => {
    if (!currentLoad || !session) return;
    const load = parseFloat(currentLoad);
    if (isNaN(load) || load <= 0) return;
    const athleteData = [];
    Object.entries(session.athlete_statuses).forEach(([aid, info]) => {
      if (info.status !== 'ACTIVE') return;
      const inp = athleteInputs[aid];
      if (!inp?.v_mean) return;
      athleteData.push({
        athlete_id: aid,
        v_mean: parseFloat(inp.v_mean),
        v_peak: parseFloat(inp.v_peak || inp.v_mean),
        reps: parseInt(inp.reps) || 1,
      });
    });
    if (athleteData.length === 0) return;
    setSaving(true);
    try {
      const r = await api.put(`/vbt/group-sessions/${session.id}/load-step`, { load_kg: load, athletes: athleteData });
      setSession(r.data);
      setAthleteInputs({});
      setCurrentLoad('');
    } catch (e) { alert(e.response?.data?.detail || e.message); }
    setSaving(false);
  };

  const toggleAthleteStatus = async (athleteId, newStatus) => {
    try {
      const r = await api.put(`/vbt/group-sessions/${session.id}/athletes/${athleteId}/status`, { status: newStatus });
      setSession(r.data);
    } catch (e) { alert(e.response?.data?.detail || e.message); }
  };

  const finalizeSession = async () => {
    if (!session) return;
    setSaving(true);
    try {
      const r = await api.post(`/vbt/group-sessions/${session.id}/finalize`);
      setResults(r.data);
      // Fetch comparisons for created tests
      try {
        const testIds = r.data.athlete_results.map(ar => ar.test_id);
        const compRes = await api.post('/vbt/comparison/batch', testIds);
        setComparisons(compRes.data);
      } catch { setComparisons({}); }
      setStep('results');
    } catch (e) { alert(e.response?.data?.detail || e.message); }
    setSaving(false);
  };

  const updateInput = (aid, field, value) => {
    setAthleteInputs(prev => ({
      ...prev,
      [aid]: { ...(prev[aid] || {}), [field]: value },
    }));
  };

  const activeAthletes = session
    ? Object.entries(session.athlete_statuses).filter(([, v]) => v.status === 'ACTIVE')
    : [];
  const stoppedAthletes = session
    ? Object.entries(session.athlete_statuses).filter(([, v]) => v.status === 'STOPPED' || v.status === 'COMPLETED')
    : [];
  const excludedAthletes = session
    ? Object.entries(session.athlete_statuses).filter(([, v]) => v.status === 'EXCLUDED')
    : [];

  // ─── STEP 1: SETUP ───
  if (step === 'setup') {
    return (
      <div className="space-y-6 fade-in max-w-3xl mx-auto" data-testid="group-vbt-setup">
        <div className="flex items-center gap-3">
          <Button variant="ghost" size="sm" onClick={() => navigate('/staff/vbt')} data-testid="back-btn">
            <ArrowLeft className="w-4 h-4" />
          </Button>
          <div>
            <h1 className="text-2xl font-bold tracking-tight text-zinc-100 flex items-center gap-2">
              <Users className="w-6 h-6 text-amber-500" />
              {t('Group VBT Test', 'Test VBT di Gruppo')}
            </h1>
            <p className="text-sm text-zinc-500 mt-1">{t('Step 1: Test Setup', 'Fase 1: Configurazione Test')}</p>
          </div>
        </div>

        <Card className="bg-zinc-900/60 border-zinc-800">
          <CardHeader><CardTitle className="text-zinc-100">{t('Test Configuration', 'Configurazione Test')}</CardTitle></CardHeader>
          <CardContent className="space-y-5">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>{t('Team', 'Squadra')}</Label>
                <Select value={setup.team_id} onValueChange={v => setSetup(p => ({ ...p, team_id: v }))} data-testid="setup-team">
                  <SelectTrigger className="bg-zinc-950 border-zinc-800" data-testid="setup-team-trigger">
                    <SelectValue placeholder={t('Select team', 'Seleziona squadra')} />
                  </SelectTrigger>
                  <SelectContent className="bg-zinc-900 border-zinc-800">
                    {teams.map(tm => <SelectItem key={tm.id} value={tm.id}>{tm.code} - {tm.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>{t('Date', 'Data')}</Label>
                <Input type="date" value={setup.date} onChange={e => setSetup(p => ({ ...p, date: e.target.value }))}
                  className="bg-zinc-950 border-zinc-800" data-testid="setup-date" />
              </div>
              <div className="space-y-2">
                <Label>{t('Exercise', 'Esercizio')}</Label>
                <Select value={setup.exercise_id} onValueChange={v => setSetup(p => ({ ...p, exercise_id: v }))} data-testid="setup-exercise">
                  <SelectTrigger className="bg-zinc-950 border-zinc-800" data-testid="setup-exercise-trigger">
                    <SelectValue placeholder={t('Select exercise', 'Seleziona esercizio')} />
                  </SelectTrigger>
                  <SelectContent className="bg-zinc-900 border-zinc-800">
                    {exercises.map(ex => (
                      <SelectItem key={ex.id} value={ex.id}>
                        {ex.name} {ex.vbt_lift_type ? `(${ex.vbt_lift_type})` : ''}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="space-y-2">
              <Label>{t('Notes', 'Note')}</Label>
              <Textarea value={setup.notes} onChange={e => setSetup(p => ({ ...p, notes: e.target.value }))}
                className="bg-zinc-950 border-zinc-800 min-h-[60px]" data-testid="setup-notes" />
            </div>
          </CardContent>
        </Card>

        {setup.team_id && athletes.length > 0 && (
          <Card className="bg-zinc-900/60 border-zinc-800">
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle className="text-zinc-100">{t('Athletes', 'Atleti')} ({athletes.length})</CardTitle>
                <div className="flex gap-2">
                  <Button variant="outline" size="sm" className="border-zinc-700 text-zinc-400 hover:text-zinc-100"
                    onClick={() => { const s = {}; athletes.forEach(a => { s[a.id] = true; }); setSelectedAthletes(s); }}
                    data-testid="select-all-btn">{t('Select All', 'Tutti')}</Button>
                  <Button variant="outline" size="sm" className="border-zinc-700 text-zinc-400 hover:text-zinc-100"
                    onClick={() => setSelectedAthletes({})}
                    data-testid="deselect-all-btn">{t('Deselect All', 'Nessuno')}</Button>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                {athletes.map(a => {
                  const selected = selectedAthletes[a.id];
                  return (
                    <button key={a.id}
                      onClick={() => setSelectedAthletes(p => ({ ...p, [a.id]: !p[a.id] }))}
                      className={`p-3 rounded-sm border text-left transition-all ${
                        selected
                          ? 'border-amber-500/50 bg-amber-500/10 text-zinc-100'
                          : 'border-zinc-800 bg-zinc-950 text-zinc-500'
                      }`} data-testid={`athlete-toggle-${a.id}`}>
                      <div className="font-medium text-sm">{a.first_name} {a.last_name}</div>
                      <div className="text-xs mt-0.5 opacity-70">{a.primary_position} {a.jersey_number ? `#${a.jersey_number}` : ''}</div>
                    </button>
                  );
                })}
              </div>
            </CardContent>
          </Card>
        )}

        <div className="flex justify-end">
          <Button onClick={handleStartSession} disabled={!setup.team_id || !setup.exercise_id || loading || Object.values(selectedAthletes).filter(Boolean).length < 1}
            className="bg-amber-500 text-zinc-950 hover:bg-amber-400 px-8" data-testid="start-session-btn">
            {loading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <ChevronRight className="w-4 h-4 mr-2" />}
            {t('Start Testing Session', 'Inizia Sessione di Test')}
          </Button>
        </div>
      </div>
    );
  }

  // ─── STEP 2: TESTING ───
  if (step === 'testing' && session) {
    const loadSteps = session.load_steps || [];
    return (
      <div className="space-y-5 fade-in" data-testid="group-vbt-testing">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <div>
            <h1 className="text-2xl font-bold tracking-tight text-zinc-100 flex items-center gap-2">
              <Dumbbell className="w-6 h-6 text-amber-500" />
              {session.exercise_name || session.lift_type}
              <span className="text-base font-normal text-zinc-500 ml-2">{session.date}</span>
            </h1>
            <p className="text-sm text-zinc-500 mt-1">
              {t(`Step 2: Testing — ${loadSteps.length} load steps recorded`, `Fase 2: Test — ${loadSteps.length} step di carico registrati`)}
            </p>
          </div>
          <div className="flex items-center gap-3">
            <div className="text-sm text-zinc-400">
              <span className="text-emerald-400 font-semibold">{activeAthletes.length}</span> {t('active', 'attivi')}
              {stoppedAthletes.length > 0 && <> · <span className="text-amber-400 font-semibold">{stoppedAthletes.length}</span> {t('stopped', 'fermati')}</>}
            </div>
            <Button onClick={finalizeSession} disabled={loadSteps.length < 1 || saving}
              className="bg-emerald-600 text-white hover:bg-emerald-500" data-testid="finalize-btn">
              {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Check className="w-4 h-4 mr-2" />}
              {t('Finalize & Save', 'Concludi e Salva')}
            </Button>
          </div>
        </div>

        {/* Completed Load Steps */}
        {loadSteps.length > 0 && (
          <Card className="bg-zinc-900/40 border-zinc-800/60">
            <CardHeader className="py-3 px-4">
              <CardTitle className="text-sm text-zinc-400 uppercase tracking-wider">{t('Recorded Steps', 'Step Registrati')}</CardTitle>
            </CardHeader>
            <CardContent className="px-4 pb-3">
              <div className="flex flex-wrap gap-2">
                {loadSteps.map((s, i) => (
                  <div key={i} className="px-3 py-2 bg-zinc-800/60 rounded-sm border border-zinc-700/50 text-sm">
                    <span className="font-bold text-amber-400">{s.load_kg} kg</span>
                    <span className="text-zinc-500 ml-2">{Object.keys(s.athletes || {}).length} {t('athletes', 'atleti')}</span>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {/* New Load Step Input */}
        <Card className="bg-zinc-900/60 border-amber-500/30" data-testid="load-step-card">
          <CardHeader className="py-3 px-4 border-b border-zinc-800">
            <div className="flex items-center justify-between">
              <CardTitle className="text-zinc-100 text-base flex items-center gap-2">
                <Plus className="w-4 h-4 text-amber-500" />
                {t('New Load Step', 'Nuovo Step di Carico')}
              </CardTitle>
              <div className="flex items-center gap-2">
                <Label className="text-sm text-zinc-400">{t('Load (kg)', 'Carico (kg)')}</Label>
                <Input type="number" value={currentLoad} onChange={e => setCurrentLoad(e.target.value)}
                  className="w-24 bg-zinc-950 border-zinc-700 text-center font-bold text-lg"
                  placeholder="0" data-testid="load-input" />
                <span className="text-zinc-500 text-sm">kg</span>
              </div>
            </div>
          </CardHeader>
          <CardContent className="px-0 pb-0">
            {/* Table header */}
            <div className="grid grid-cols-[1fr_100px_100px_70px_80px] gap-1 px-4 py-2 bg-zinc-800/30 text-xs text-zinc-500 uppercase tracking-wider font-medium border-b border-zinc-800">
              <div>{t('Athlete', 'Atleta')}</div>
              <div className="text-center">V mean</div>
              <div className="text-center">V peak</div>
              <div className="text-center">{t('Reps', 'Rip')}</div>
              <div className="text-center">{t('Action', 'Azione')}</div>
            </div>
            {/* Active athletes */}
            {activeAthletes.map(([aid, info]) => (
              <div key={aid}
                className="grid grid-cols-[1fr_100px_100px_70px_80px] gap-1 items-center px-4 py-2.5 border-b border-zinc-800/40 hover:bg-zinc-800/20 transition-colors"
                data-testid={`athlete-row-${aid}`}>
                <div>
                  <span className="font-medium text-zinc-100 text-sm">{info.name}</span>
                  {info.position && <span className="text-xs text-zinc-500 ml-2">{info.position}</span>}
                </div>
                <Input type="number" step="0.01" placeholder="m/s"
                  value={athleteInputs[aid]?.v_mean || ''}
                  onChange={e => updateInput(aid, 'v_mean', e.target.value)}
                  className="h-8 bg-zinc-950 border-zinc-700 text-center text-sm"
                  data-testid={`v-mean-${aid}`} />
                <Input type="number" step="0.01" placeholder="m/s"
                  value={athleteInputs[aid]?.v_peak || ''}
                  onChange={e => updateInput(aid, 'v_peak', e.target.value)}
                  className="h-8 bg-zinc-950 border-zinc-700 text-center text-sm"
                  data-testid={`v-peak-${aid}`} />
                <Input type="number" min="1" placeholder="1"
                  value={athleteInputs[aid]?.reps || ''}
                  onChange={e => updateInput(aid, 'reps', e.target.value)}
                  className="h-8 bg-zinc-950 border-zinc-700 text-center text-sm"
                  data-testid={`reps-${aid}`} />
                <div className="flex justify-center">
                  <Button variant="ghost" size="sm"
                    onClick={() => toggleAthleteStatus(aid, 'STOPPED')}
                    className="h-7 px-2 text-amber-400 hover:text-amber-300 hover:bg-amber-500/10"
                    title={t('Stop athlete', 'Ferma atleta')} data-testid={`stop-btn-${aid}`}>
                    <Square className="w-3.5 h-3.5" />
                  </Button>
                </div>
              </div>
            ))}
            {/* Stopped athletes (greyed out) */}
            {stoppedAthletes.map(([aid, info]) => (
              <div key={aid}
                className="grid grid-cols-[1fr_100px_100px_70px_80px] gap-1 items-center px-4 py-2.5 border-b border-zinc-800/40 opacity-40"
                data-testid={`athlete-row-stopped-${aid}`}>
                <div>
                  <span className="font-medium text-zinc-400 text-sm">{info.name}</span>
                  <span className={`ml-2 text-xs px-1.5 py-0.5 rounded border ${STATUS_COLORS[info.status]}`}>{info.status}</span>
                  {info.stopped_at_load && <span className="text-xs text-zinc-600 ml-1">@ {info.stopped_at_load}kg</span>}
                </div>
                <div /><div /><div />
                <div className="flex justify-center">
                  {info.status === 'STOPPED' && (
                    <Button variant="ghost" size="sm"
                      onClick={() => toggleAthleteStatus(aid, 'ACTIVE')}
                      className="h-7 px-2 text-emerald-400 hover:text-emerald-300 hover:bg-emerald-500/10"
                      title={t('Reactivate', 'Riattiva')} data-testid={`reactivate-btn-${aid}`}>
                      <Plus className="w-3.5 h-3.5" />
                    </Button>
                  )}
                </div>
              </div>
            ))}
          </CardContent>
        </Card>

        {/* Record button */}
        <div className="flex justify-center">
          <Button onClick={addLoadStep}
            disabled={!currentLoad || saving || activeAthletes.length === 0}
            className="bg-amber-500 text-zinc-950 hover:bg-amber-400 px-10 py-5 text-base font-bold"
            data-testid="record-step-btn">
            {saving ? <Loader2 className="w-5 h-5 mr-2 animate-spin" /> : <Save className="w-5 h-5 mr-2" />}
            {t(`Record ${currentLoad || '—'} kg`, `Registra ${currentLoad || '—'} kg`)}
          </Button>
        </div>
      </div>
    );
  }

  // ─── STEP 3: RESULTS ───
  if (step === 'results' && results) {
    return (
      <div className="space-y-6 fade-in max-w-3xl mx-auto" data-testid="group-vbt-results">
        <div className="text-center space-y-2">
          <div className="inline-flex items-center justify-center w-14 h-14 rounded-full bg-emerald-500/20 border border-emerald-500/30">
            <Check className="w-7 h-7 text-emerald-400" />
          </div>
          <h1 className="text-2xl font-bold text-zinc-100">{t('Session Complete', 'Sessione Completata')}</h1>
          <p className="text-zinc-500">
            {t(`${results.tests_created} individual tests created`, `${results.tests_created} test individuali creati`)}
          </p>
        </div>

        <div className="space-y-3">
          {results.athlete_results.map(r => (
            <Card key={r.test_id} className="bg-zinc-900/60 border-zinc-800 hover:border-zinc-700 transition-colors cursor-pointer"
              onClick={() => navigate(`/staff/vbt/tests/${r.test_id}`)} data-testid={`result-card-${r.athlete_id}`}>
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <div className="font-semibold text-zinc-100">{r.athlete_name}</div>
                    <div className="text-sm text-zinc-500 mt-1">
                      {r.data_points} {t('data points', 'punti dati')}
                    </div>
                  </div>
                  <div className="text-right">
                    {r.lv_profile ? (
                      <>
                        <div className="text-sm">
                          <span className="text-zinc-500">R² = </span>
                          <span className={`font-bold ${r.lv_profile.r_squared >= 0.95 ? 'text-emerald-400' : r.lv_profile.r_squared >= 0.9 ? 'text-amber-400' : 'text-red-400'}`}>
                            {r.lv_profile.r_squared.toFixed(2)}
                          </span>
                        </div>
                        <div className="text-xs text-zinc-500 mt-0.5">
                          V0={r.lv_profile.v0.toFixed(1)} · L0={r.lv_profile.l0.toFixed(1)}kg
                        </div>
                      </>
                    ) : (
                      <span className="text-xs text-zinc-600">{t('No profile', 'Nessun profilo')}</span>
                    )}
                    {r.quality_report && !r.quality_report.is_valid && (
                      <div className="flex items-center gap-1 text-xs text-red-400 mt-1 justify-end">
                        <AlertTriangle className="w-3 h-3" /> {t('Issues detected', 'Problemi rilevati')}
                      </div>
                    )}
                  </div>
                  <ChevronRight className="w-4 h-4 text-zinc-600 ml-3" />
                </div>
                {/* Previous test comparison */}
                {(() => {
                  const comp = comparisons[r.test_id];
                  if (!comp || !comp.has_previous) return null;
                  const d = comp.deltas;
                  const items = [
                    d.l0 && { label: 'L0', val: `${comp.previous.l0?.toFixed(1)}\u2192${comp.current.l0?.toFixed(1)}`, pct: d.l0.pct, dir: d.l0.direction },
                    d.v0 && { label: 'V0', val: `${comp.previous.v0?.toFixed(2)}\u2192${comp.current.v0?.toFixed(2)}`, pct: d.v0.pct, dir: d.v0.direction },
                    d.r_squared && { label: 'R\u00b2', val: `${comp.previous.r_squared?.toFixed(2)}\u2192${comp.current.r_squared?.toFixed(2)}`, pct: d.r_squared.pct, dir: d.r_squared.direction },
                  ].filter(Boolean);
                  if (items.length === 0) return null;
                  return (
                    <div className="flex items-center gap-3 mt-2 pt-2 border-t border-zinc-800/40 text-[11px]" data-testid={`group-comp-${r.athlete_id}`}>
                      <TrendingUp className="w-3 h-3 text-purple-400 flex-shrink-0" />
                      <span className="text-zinc-600">{t('vs', 'vs')} {comp.previous.date}:</span>
                      {items.map(it => (
                        <span key={it.label} className={it.dir === 'improved' ? 'text-emerald-400' : it.dir === 'declined' ? 'text-rose-400' : 'text-zinc-500'}>
                          {it.label} {it.val} ({it.pct > 0 ? '+' : ''}{it.pct}%{it.dir === 'improved' ? '\u2191' : it.dir === 'declined' ? '\u2193' : '\u2192'})
                        </span>
                      ))}
                    </div>
                  );
                })()}
              </CardContent>
            </Card>
          ))}
        </div>

        <div className="flex justify-center gap-3 pt-2">
          <Button variant="outline" onClick={() => navigate('/staff/vbt')}
            className="border-zinc-700 text-zinc-300" data-testid="back-to-vbt-btn">
            {t('Back to VBT', 'Torna a VBT')}
          </Button>
          <Button onClick={() => { setStep('setup'); setSession(null); setResults(null); setCurrentLoad(''); setAthleteInputs({}); }}
            className="bg-amber-500 text-zinc-950 hover:bg-amber-400" data-testid="new-session-btn">
            <Plus className="w-4 h-4 mr-2" />
            {t('New Group Test', 'Nuovo Test di Gruppo')}
          </Button>
        </div>
      </div>
    );
  }

  return null;
}
